import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	Output
} from "@angular/core";
import { BPInputModel } from "@modules/core/components/bp-input/bp-input.component";
import { BehaviorSubject, combineLatest, fromEvent } from "rxjs";
import { debounceTime, map } from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { GOOGLE_SEARCH_DEBOUNCE_TIME } from "@helpers/constants";
import { GooglePlacesService } from "@modules/shared/bp-google-autocomplete/bp-google-autocomplete.facade";
import { ProfileWithValidateable } from "@modules/main/components/profile/new-profile/entities/profile-model";

@UntilDestroy()
@Component({
	selector: "bp-place-of-birth-autocomplete",
	templateUrl: "./bp-place-of-birth-autocomplete.component.html",
	styleUrls: ["./bp-place-of-birth-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class BpPlaceOfBirthAutocompleteComponent implements AfterViewInit {
	@Input() label = "";
	@Input() placeholder = "";
	@Input() model: BPInputModel = { clearError: () => undefined, hasError: () => false, wiggle: () => "" };
	@Input() profile: ProfileWithValidateable;
	@Output() public onPlaceOfBirthUpdate: EventEmitter<string> = new EventEmitter<string>();
	@Output() public cityNotFound: EventEmitter<void> = new EventEmitter<void>();

	public inputGroup: ElementRef;
	public predictions: google.maps.places.AutocompletePrediction[] = [];
	public showSuggestions = false;
	public hasInput = false;
	public place_of_birth = "";

	private sessionToken: google.maps.places.AutocompleteSessionToken = null;
	private searchFinished$ = new BehaviorSubject<boolean>(true);

	public state$ = combineLatest([this.searchFinished$]).pipe(map(([searchFinished]) => ({ searchFinished })));

	constructor(private googlePlacesService: GooglePlacesService) {}

	ngAfterViewInit(): void {
		this.place_of_birth = this.profile?.place_of_birth || "";
		fromEvent(this.inputGroup.nativeElement, "keyup")
			.pipe(debounceTime(GOOGLE_SEARCH_DEBOUNCE_TIME), untilDestroyed(this))
			.subscribe(async () => {
				const value = this.inputGroup.nativeElement.value;

				this.hasInput = value.length > 0;
				this.showSuggestions = this.hasInput;

				if (!this.hasInput) {
					this.predictions = [];
					return;
				}

				this.searchFinished$.next(false);

				this.sessionToken = await this.googlePlacesService.getSessionToken(this.sessionToken);
				this.predictions = await this.googlePlacesService.getCityPredictions(value, this.sessionToken);
				this.searchFinished$.next(true);

				if (this.predictions.length === 0) {
					this.cityNotFound.emit();
				}
			});
	}

	async searchResultClick(prediction: google.maps.places.AutocompletePrediction, event: Event): Promise<void> {
		event.preventDefault();

		this.sessionToken = await this.googlePlacesService.getSessionToken(this.sessionToken);
		const place = await this.googlePlacesService.getPlaceDetails(prediction.place_id, this.sessionToken);

		const city = place.getCity();
		const country = place.getCountry();
		const result = [city, country].filter(Boolean).join(", ");

		this.onPlaceOfBirthUpdate.emit(result);
		this.profile.place_of_birth = result;
		this.inputGroup.nativeElement.value = result;

		this.showSuggestions = false;
		this.predictions = [];
		this.sessionToken = null;
	}

	updateHasInput(value: string): void {
		this.hasInput = value.length > 0;
	}

	handleFocusOut(): void {
		this.showSuggestions = false;
	}
}
